// src/ui/kit.jsx
// The shared React pieces of the design system (styles live in tokens.css).
// Every page builds from these so headers, cards, numbers, badges and controls
// look and behave the same everywhere.

import React, { useState } from "react";
import { getHeroImageUrl } from "../utils/heroCatalog";
import { pct } from "./format";

const cx = (...parts) => parts.filter(Boolean).join(" ");

/* ── layout ─────────────────────────────────────────────────────────────── */

/** Page body inside the shell. `wide` drops the max width, `flush` drops padding too (full-bleed tools). */
export function Page({ children, wide = false, flush = false, className, style }) {
  return (
    <div className={cx("ds-page", wide && "ds-page--wide", flush && "ds-page--flush", "ds-fade-in", className)} style={style}>
      {children}
    </div>
  );
}

export function PageHeader({ eyebrow, icon: Icon, title, subtitle, actions, children, style }) {
  return (
    <header className="ds-page-head" style={style}>
      <div style={{ minWidth: 0 }}>
        {eyebrow ? (
          <div className="ds-eyebrow">
            {Icon ? <Icon size={13} strokeWidth={2.2} /> : null}
            {eyebrow}
          </div>
        ) : null}
        <h1 className="ds-page-title">{title}</h1>
        {subtitle ? <div className="ds-page-sub">{subtitle}</div> : null}
        {children}
      </div>
      {actions ? <div className="ds-page-actions">{actions}</div> : null}
    </header>
  );
}

export function Section({ title, desc, actions, children, style }) {
  return (
    <section style={style}>
      <div className="ds-section-title">
        <div>
          <h2>{title}</h2>
          {desc ? <p>{desc}</p> : null}
        </div>
        {actions ? <div className="ds-card-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function Card({
  title,
  subtitle,
  icon: Icon,
  actions,
  footer,
  children,
  tone,
  className,
  style,
  bodyStyle,
  bodyClassName,
  noBody = false,
}) {
  const hasHead = title || subtitle || actions;
  return (
    <div className={cx("ds-card", tone === "accent" && "ds-card--accent", tone === "raised" && "ds-card--raised", className)} style={style}>
      {hasHead ? (
        <div className="ds-card-head">
          <div style={{ minWidth: 0 }}>
            {title ? (
              <h3 className="ds-card-title">
                {Icon ? <Icon size={16} strokeWidth={2.2} /> : null}
                {title}
              </h3>
            ) : null}
            {subtitle ? <p className="ds-card-sub">{subtitle}</p> : null}
          </div>
          {actions ? <div className="ds-card-actions">{actions}</div> : null}
        </div>
      ) : null}
      {noBody ? children : <div className={cx("ds-card-body", bodyClassName)} style={bodyStyle}>{children}</div>}
      {footer ? <div className="ds-card-foot">{footer}</div> : null}
    </div>
  );
}

/* ── numbers ────────────────────────────────────────────────────────────── */

/** Headline number. `aside` sits right of the value (ring, gauge); `chart` sits under it (spark bars, sparkline…). */
export function Kpi({ label, icon: Icon, value, unit, sub, tone, meter, chart, aside, title, style }) {
  const head = (
    <>
      <div className="ds-kpi-label">
        {Icon ? <Icon size={14} strokeWidth={2.2} /> : null}
        {label}
      </div>
      <div className="ds-kpi-value">
        {value}
        {unit ? <small>{unit}</small> : null}
      </div>
    </>
  );
  return (
    <div className="ds-kpi" data-tone={tone} title={title} style={style}>
      {aside ? (
        <div className="ds-kpi-top">
          <div style={{ minWidth: 0 }}>{head}</div>
          <div className="ds-kpi-aside">{aside}</div>
        </div>
      ) : (
        head
      )}
      {meter ? <div className="ds-kpi-meter">{meter}</div> : null}
      {chart ? <div className="ds-kpi-chart">{chart}</div> : null}
      {sub ? <div className="ds-kpi-sub">{sub}</div> : null}
    </div>
  );
}

/** Signed change with an arrow. `value` in the unit you pass via `suffix` (e.g. percentage points). */
export function Delta({ value, digits = 1, suffix = "", invert = false, title }) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const n = Number(value);
  const flat = Math.abs(n) < 10 ** -digits / 2;
  const good = invert ? n < 0 : n > 0;
  const dir = flat ? "flat" : good ? "up" : "down";
  const arrow = flat ? "→" : n > 0 ? "▲" : "▼";
  return (
    <span className="ds-delta" data-dir={dir} title={title}>
      {arrow} {Math.abs(n).toFixed(digits)}
      {suffix}
    </span>
  );
}

/* ── controls ───────────────────────────────────────────────────────────── */

export function Button({ variant, size, icon: Icon, iconRight: IconRight, children, className, type = "button", ...rest }) {
  const iconOnly = Icon && (children == null || children === false);
  return (
    <button
      type={type}
      className={cx(
        "ds-btn",
        variant && `ds-btn--${variant}`,
        size && `ds-btn--${size}`,
        iconOnly && "ds-btn--icon",
        className
      )}
      {...rest}
    >
      {Icon ? <Icon size={size === "sm" ? 13 : 15} strokeWidth={2.2} /> : null}
      {children}
      {IconRight ? <IconRight size={size === "sm" ? 13 : 15} strokeWidth={2.2} /> : null}
    </button>
  );
}

/** Pill group for 2–6 exclusive choices. options: [{ value, label, icon? }] */
export function Segmented({ options, value, onChange, accent = false, ariaLabel, style }) {
  return (
    <div className={cx("ds-seg", accent && "ds-seg--accent")} role="group" aria-label={ariaLabel} style={style}>
      {options.map((o) => {
        const Icon = o.icon;
        return (
          <button key={String(o.value)} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)} title={o.title}>
            {Icon ? <Icon size={13} strokeWidth={2.2} /> : null}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Underlined tabs. tabs: [{ id, label, icon?, count? }] */
export function Tabs({ tabs, value, onChange, style, ariaLabel }) {
  return (
    <div className="ds-tabs" role="tablist" aria-label={ariaLabel} style={style}>
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <button key={t.id} type="button" role="tab" className="ds-tab" aria-selected={t.id === value} onClick={() => onChange(t.id)} title={t.title}>
            {Icon ? <Icon size={15} strokeWidth={2.1} /> : null}
            {t.label}
            {t.count != null ? <span className="ds-tab-count">{t.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function Field({ label, children, style, hint }) {
  return (
    <label className="ds-field" style={style}>
      {label ? <span className="ds-label">{label}</span> : null}
      {children}
      {hint ? <span className="ds-muted" style={{ fontSize: 11.5 }}>{hint}</span> : null}
    </label>
  );
}

export function SearchInput({ value, onChange, placeholder = "ค้นหา…", icon: Icon, style, inputStyle, ...rest }) {
  return (
    <div className="ds-input-wrap" style={style}>
      {Icon ? <Icon size={14} strokeWidth={2.2} /> : null}
      <input className="ds-input" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} {...rest} />
    </div>
  );
}

/* ── badges ─────────────────────────────────────────────────────────────── */

export function Badge({ tone, dot = false, outline = false, children, title, style }) {
  return (
    <span className={cx("ds-badge", tone && `ds-badge--${tone}`, outline && "ds-badge--outline")} title={title} style={style}>
      {dot ? <i className="ds-badge-dot" /> : null}
      {children}
    </span>
  );
}

/** Win / loss with a word, never colour alone. */
export function ResultBadge({ win, unknown = false, short = false }) {
  if (unknown) return <Badge outline>{short ? "?" : "ไม่ทราบ"}</Badge>;
  return win ? (
    <Badge tone="win" dot>
      {short ? "W" : "ชนะ"}
    </Badge>
  ) : (
    <Badge tone="loss" dot>
      {short ? "L" : "แพ้"}
    </Badge>
  );
}

export function SideBadge({ side }) {
  const s = String(side || "").trim().toLowerCase();
  if (s === "blue") return <Badge tone="blue">Blue</Badge>;
  if (s === "red") return <Badge tone="red">Red</Badge>;
  return <span className="ds-muted">–</span>;
}

/* ── meters ─────────────────────────────────────────────────────────────── */

export function Meter({ value, color = "var(--accent)", style, title }) {
  const v = Math.max(0, Math.min(1, Number(value) || 0));
  return (
    <div className="ds-meter" style={style} title={title}>
      <span style={{ width: `${v * 100}%`, background: color }} />
    </div>
  );
}

/** Wins | losses split bar with a 2px gap. */
export function WinLossBar({ wins = 0, losses = 0, height = 6, style }) {
  const total = wins + losses;
  if (!total) return <div className="ds-meter" style={{ height, ...style }} />;
  return (
    <div className="ds-meter ds-meter--split" style={{ height, ...style }} title={`ชนะ ${wins} · แพ้ ${losses} (${pct(wins / total)})`}>
      {wins > 0 ? <span style={{ width: `${(wins / total) * 100}%`, background: "var(--win)" }} /> : null}
      {losses > 0 ? <span style={{ width: `${(losses / total) * 100}%`, background: "var(--loss)" }} /> : null}
    </div>
  );
}

/** Recent results as W/L squares. results: [{ win: bool, title?: string }] oldest → newest */
export function FormStrip({ results, style }) {
  return (
    <div className="ds-form" style={style}>
      {results.map((r, i) => (
        <i key={i} data-r={r.win ? "W" : "L"} title={r.title}>
          {r.win ? "W" : "L"}
        </i>
      ))}
    </div>
  );
}

/* ── heroes ─────────────────────────────────────────────────────────────── */

export function HeroAvatar({ name, size = 28, role, title, style }) {
  const [broken, setBroken] = useState(false);
  const src = name ? getHeroImageUrl(name, role) : null;
  if (!src || broken) {
    return (
      <span
        className="ds-hero ds-hero--fallback"
        style={{ width: size, height: size, fontSize: Math.max(9, size * 0.36), ...style }}
        title={title ?? name}
      >
        {String(name || "?").slice(0, 2)}
      </span>
    );
  }
  return (
    <img
      className="ds-hero"
      src={src}
      alt={name}
      title={title ?? name}
      width={size}
      height={size}
      loading="lazy"
      onError={() => setBroken(true)}
      style={{ width: size, height: size, borderRadius: Math.max(5, size * 0.24), ...style }}
    />
  );
}

export function HeroStack({ names = [], size = 24, max = 5, roles = [], dim = false }) {
  const list = names.filter(Boolean).slice(0, max);
  if (!list.length) return <span className="ds-muted">–</span>;
  return (
    <span className="ds-hero-stack" style={dim ? { opacity: 0.6, filter: "grayscale(0.4)" } : undefined}>
      {list.map((n, i) => (
        <HeroAvatar key={`${n}-${i}`} name={n} size={size} role={roles[i]} />
      ))}
    </span>
  );
}

/* ── feedback ───────────────────────────────────────────────────────────── */

export function EmptyState({ icon: Icon, title, children, action, style }) {
  return (
    <div className="ds-empty" style={style}>
      {Icon ? (
        <div className="ds-empty-icon">
          <Icon size={22} strokeWidth={2} />
        </div>
      ) : null}
      {title ? <h3>{title}</h3> : null}
      {children ? <p>{children}</p> : null}
      {action ? <div style={{ marginTop: 8 }}>{action}</div> : null}
    </div>
  );
}

export function Callout({ tone, icon: Icon, children, style }) {
  return (
    <div className={cx("ds-callout", tone && `ds-callout--${tone}`)} style={style}>
      {Icon ? <Icon size={15} strokeWidth={2.2} /> : null}
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

export function Legend({ items, style }) {
  return (
    <div className="ds-legend" style={style}>
      {items.map((it) => (
        <span key={it.label}>
          <i style={{ background: it.color }} data-shape={it.shape} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/* ── react-select in the dark theme ─────────────────────────────────────── */

export const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    borderRadius: 8,
    background: "var(--bg-elev)",
    borderColor: state.isFocused ? "var(--accent-line)" : "var(--border-2)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(255,107,26,0.14)" : "none",
    fontSize: 13,
    "&:hover": { borderColor: "var(--border-3)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "1px 8px" }),
  input: (base) => ({ ...base, color: "var(--text)", margin: 0 }),
  placeholder: (base) => ({ ...base, color: "var(--text-4)" }),
  singleValue: (base) => ({ ...base, color: "var(--text)" }),
  multiValue: (base) => ({ ...base, background: "var(--accent-soft)", borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: "var(--accent-ink)", fontSize: 12, padding: "1px 6px" }),
  multiValueRemove: (base) => ({ ...base, color: "var(--accent-ink)", borderRadius: 6, ":hover": { background: "rgba(255,107,26,0.3)", color: "#fff" } }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, padding: 6, color: "var(--text-3)" }),
  clearIndicator: (base) => ({ ...base, padding: 6, color: "var(--text-3)" }),
  menu: (base) => ({
    ...base,
    background: "var(--surface-2)",
    border: "1px solid var(--border-2)",
    borderRadius: 10,
    boxShadow: "var(--shadow-pop)",
    overflow: "hidden",
    zIndex: 40,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1000 }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 6,
    fontSize: 13,
    background: state.isSelected ? "var(--accent-soft)" : state.isFocused ? "var(--surface-3)" : "transparent",
    color: state.isSelected ? "var(--accent-ink)" : "var(--text)",
    cursor: "pointer",
  }),
  noOptionsMessage: (base) => ({ ...base, color: "var(--text-3)", fontSize: 12.5 }),
};
